type Alert = {
  type: 'neutral' | 'error' | 'informative' | 'success' | 'warning'; // Restrict to specific alert types
  title?: string;
  description?: string;
  iconLeft?: { category?: string; name?: string }; // Optional property for the shape/type of the iconLeft prop
  gaText?: string; // Optional property for analytics
  id?: string; // Optional, will be auto-generated if not provided
  dismiss?: boolean;
  showDismiss?: boolean; // Whether to show the close button
};

type Subscriber = (data: Alert | { id: string; dismiss: boolean }) => void;

type DismissEvent = { id: string; dismiss: true }; // Type for dismissal events

const createAlertManager = () => {
  let subscribers: Subscriber[] = [];
  let alerts: Alert[] = [];
  let alertCounter = 0;

  const subscribe = (subscriber: Subscriber): (() => void) => {
    subscribers.push(subscriber);
    return () => {
      subscribers = subscribers.filter((sub) => sub !== subscriber);
    };
  };

  const publish = (data: Alert | DismissEvent): void => {
    subscribers.forEach((subscriber) => subscriber(data));
  };

  const addAlert = (data: Alert): string => {
    const id = String(data?.id || alertCounter++);
    const alert = { ...data, id };
    alerts.push(alert);
    publish(alert);
    return id;
  };

  const dismiss = (id: string): void => {
    alerts = alerts.filter((alert) => alert.id !== id);
    publish({ id, dismiss: true });
  };

  const dismissAll = (): void => {
    const ids = alerts.map((alert) => alert.id).filter((id): id is string => id !== undefined);
    alerts = [];
    ids.forEach((id) => publish({ id, dismiss: true }));
  };

  const getAlerts = () => [...alerts];

  return {
    subscribe,
    addAlert,
    dismiss,
    dismissAll,
    getAlerts,
  };
};

// Create a default global instance
export const defaultGlobalAlertManager = createAlertManager();
export { createAlertManager };
